import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { UnumApplicationResponseDto } from 'enrollment/queries';
import { Beneficiaries } from 'enrollment/features/AddYourBeneficiaries/components/Beneficiaries';
import { useEnrollmentBeneficiaryQuery } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery';
import { ApplicationContainerStyled, BorderStyled } from '../UnumStyled';
import { setAllBeneficiaryData } from 'enrollment/features/AddYourBeneficiaries/services';
import { BeneficiarySelectedOptionsDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';

interface UnumApplicationBeneficiariesProps {
  data: UnumApplicationResponseDto;
  setTotalAllocation?: Dispatch<SetStateAction<number>>;
}

export const UnumApplicationBeneficiaries = ({
  data,
  setTotalAllocation,
}: UnumApplicationBeneficiariesProps) => {
  const { t } = useTranslation('enrollment.plugins');
  const dispatch = useDispatch();
  const setDisabled = () => {
    return;
  };

  const initialPlansState: { [key: string]: any }[] =
    data.data.application.Applicants.Applicant.map((applicant) => ({
      orgBenefitId:
        typeof applicant.DependentID === 'string' ? applicant.DependentID : '',
      orgBenefitName: applicant.ApplicantType,
      beneficiaries: [],
      primaryTotalAllocation: 0,
    }));

  useEffect(() => {
    dispatch(setAllBeneficiaryData(initialPlansState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: beneficiaryDetails, isLoading: isBeneficiaryLoading } =
    useEnrollmentBeneficiaryQuery({
      selectionOptions: true,
    });

  const metadata = beneficiaryDetails?.data?.metadata;
  let allBeneficiaries: BeneficiarySelectedOptionsDto[] =
    beneficiaryDetails?.data?.beneficiary_selection_options || [];

  const emptyPrimaryBeneficiaries = true;

  const employee = data.data.application.Applicants.Applicant.find(
    (applicant) => applicant.ApplicantType === 'Employee'
  );

  if (employee) {
    allBeneficiaries = [
      ...allBeneficiaries,
      {
        name: `${employee.FirstName} ${employee.LastName}`,
        relationship: 'Employee',
      },
    ];
  }

  return data.data.application.Applicants.Applicant.map((applicant) => {
    const filteredBeneficiaries = allBeneficiaries?.filter(
      (beneficiary) =>
        beneficiary.name.toLowerCase().trim() !==
        `${applicant.FirstName.toLowerCase()} ${applicant.LastName.toLowerCase()}`
    );
    const id =
      typeof applicant.DependentID === 'string' ? applicant.DependentID : '';

    return (
      <ApplicationContainerStyled className="p-0">
        <h3 className="pl-4 ml-1 pt-4 mb-0">
          {applicant.ApplicantType}{' '}
          {t('enrollment.plugins:unum.Application.beneficiariesTitle')}{' '}
          {applicant.ApplicantType !== 'Employee'
            ? `(${applicant.FirstName} ${applicant.LastName})`
            : ''}
        </h3>
        <BorderStyled>
          <Beneficiaries
            key={`primary-beneficiary-${id}`}
            orgBenefitId={id}
            metadata={metadata}
            allBeneficiaries={filteredBeneficiaries}
            isBeneficiaryLoading={isBeneficiaryLoading}
            isPrimary={true}
            emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
            boldHeader={true}
            hideAddToAllBenefits={true}
            setDisabled={setDisabled}
            autoSave={false}
          />
        </BorderStyled>
        <Beneficiaries
          key={`secondary-beneficiary-${id}`}
          orgBenefitId={id}
          metadata={metadata}
          allBeneficiaries={filteredBeneficiaries}
          isBeneficiaryLoading={isBeneficiaryLoading}
          isPrimary={false}
          emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
          boldHeader={true}
          hideAddToAllBenefits={true}
          setDisabled={setDisabled}
          autoSave={false}
          setTotalAllocation={setTotalAllocation}
        />
      </ApplicationContainerStyled>
    );
  });
};
